import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Form, Input, Switch, Button, Divider, Typography, Alert, Spin,
  Checkbox, Select, Row, Col, Avatar, Tag, Collapse,
} from 'antd';
import { WhatsAppOutlined, SaveOutlined, SendOutlined, UserOutlined } from '@ant-design/icons';
import WhatsAppConfigApi from '../../api/settings/WhatsAppConfigApi';
import { openNotificationSuccess, openNotificationError } from '../../helpers/NotificationHelpers';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const RAW_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/';
const NORMALIZED_BASE = RAW_BASE_URL.endsWith('/') ? RAW_BASE_URL : `${RAW_BASE_URL}/`;
const API_BASE_V1 = NORMALIZED_BASE.includes('/v1/') ? NORMALIZED_BASE : `${NORMALIZED_BASE}v1/`;

const FREQUENCY_OPTIONS = [
  { label: 'Daily (weekdays 8am)', value: 'daily' },
  { label: 'Weekly (Monday 8am)', value: 'weekly' },
  { label: 'On Completion (instant)', value: 'on_completion' },
];

const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const WhatsAppConfig = () => {
  const [configForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [config, setConfig] = useState(null);
  const [testNumber, setTestNumber] = useState('');
  const [cycles, setCycles] = useState([]);
  const [positions, setPositions] = useState([]);
  const [positionForms, setPositionForms] = useState({});
  const [savingPref, setSavingPref] = useState({});

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const existing = await WhatsAppConfigApi.getConfig();
      if (existing) {
        setConfig(existing);
        configForm.setFieldsValue({
          enabled: existing.enabled,
          fromNumber: existing.fromNumber || '',
          frontendUrl: existing.frontendUrl || '',
        });
      }
    } catch {
      openNotificationError('bottomRight', 'Failed to load WhatsApp configuration');
    } finally {
      setLoading(false);
    }
  }, [configForm]);

  const loadCycles = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_V1}performance-cycles`, { headers: getAuthHeader() });
      const data = await res.json();
      if (data.success && data.data) {
        setCycles(data.data.map(c => ({ label: c.name, value: c.id })));
      }
    } catch { /* silent */ }
  }, []);

  const loadEnabledPositions = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_V1}whatsapp-notifications/enabled-positions`, {
        headers: getAuthHeader(),
      });
      const data = await res.json();
      if (data.success) {
        setPositions(data.data || []);
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    loadConfig();
    loadCycles();
    loadEnabledPositions();
  }, [loadConfig, loadCycles, loadEnabledPositions]);

  const onSaveConfig = async (values) => {
    setSaving(true);
    try {
      const saved = await WhatsAppConfigApi.saveConfig(values);
      setConfig(saved);
      openNotificationSuccess('bottomRight', 'WhatsApp configuration saved');
    } catch {
      openNotificationError('bottomRight', 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const onSavePositionPrefs = async (positionId, values) => {
    setSavingPref(p => ({ ...p, [positionId]: true }));
    try {
      await WhatsAppConfigApi.savePreferences(positionId, values);
      openNotificationSuccess('bottomRight', 'Preferences saved');
    } catch {
      openNotificationError('bottomRight', 'Failed to save preferences');
    } finally {
      setSavingPref(p => ({ ...p, [positionId]: false }));
    }
  };

  const onTriggerSummary = async () => {
    setTriggering(true);
    try {
      const res = await fetch(`${API_BASE_V1}whatsapp-notifications/trigger-summary`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      data.success
        ? openNotificationSuccess('bottomRight', 'Plan summary triggered — messages are being sent')
        : openNotificationError('bottomRight', data.message || 'Failed to trigger summary');
    } catch {
      openNotificationError('bottomRight', 'Error triggering summary');
    } finally {
      setTriggering(false);
    }
  };

  const onTestSend = async () => {
    if (!testNumber) { openNotificationError('bottomRight', 'Enter a number to test'); return; }
    setTesting(true);
    try {
      const res = await fetch(`${API_BASE_V1}whatsapp-notifications/test-send`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: testNumber }),
      });
      const data = await res.json();
      data.success
        ? openNotificationSuccess('bottomRight', 'Test message sent — check your WhatsApp')
        : openNotificationError('bottomRight', data.message || 'Failed to send test message');
    } catch {
      openNotificationError('bottomRight', 'Error sending test message');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center p-12"><Spin size="large" /></div>;
  }

  return (
    <div className="p-6 max-w-3xl">
      <div className="mb-6 flex items-center gap-3">
        <WhatsAppOutlined style={{ fontSize: 28, color: '#25D366' }} />
        <div>
          <Title level={3} style={{ margin: 0 }}>WhatsApp Config</Title>
          <Text type="secondary">Configure Twilio credentials and per-position notification settings</Text>
        </div>
      </div>

      {config && (
        <Alert
          message={config.enabled ? 'WhatsApp integration is enabled' : 'WhatsApp integration is disabled'}
          type={config.enabled ? 'success' : 'warning'}
          showIcon
          className="mb-4"
        />
      )}

      {/* Credentials */}
      <Card className="mb-4">
        <Form form={configForm} layout="vertical" onFinish={onSaveConfig} requiredMark={false}>
          <Form.Item name="enabled" label="Enable WhatsApp Notifications" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Divider />
          <Form.Item name="accountSid" label="Twilio Account SID" extra={config?.accountSidMasked ? `Current: ${config.accountSidMasked}` : null}>
            <Input placeholder={config ? 'Leave blank to keep existing' : 'ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'} />
          </Form.Item>
          <Form.Item name="authToken" label="Twilio Auth Token" extra={config ? 'Leave blank to keep existing' : null}>
            <Input.Password placeholder={config ? 'Leave blank to keep existing' : 'Enter auth token'} />
          </Form.Item>
          <Form.Item name="fromNumber" label="From Number" extra="Format: whatsapp:+14155238886">
            <Input placeholder="whatsapp:+14155238886" />
          </Form.Item>
          <Form.Item name="frontendUrl" label="Frontend URL" extra="Base URL of your app e.g. https://app.strategizer.com — used for dashboard links in messages">
            <Input placeholder="https://app.strategizer.com" />
          </Form.Item>
          <Form.Item className="mb-0">
            <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />}>
              Save Configuration
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Test */}
      <Card title="Test Send" className="mb-4">
        <Text type="secondary" className="block mb-3">
          Send a test message. The number must have joined the Twilio sandbox first.
        </Text>
        <Row gutter={8} className="mb-3">
          <Col flex="auto">
            <Input placeholder="+94771234567" value={testNumber} onChange={e => setTestNumber(e.target.value)} />
          </Col>
          <Col>
            <Button type="primary" icon={<SendOutlined />} loading={testing} onClick={onTestSend} style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}>
              Send Test
            </Button>
          </Col>
        </Row>
        <Button type="default" onClick={onTriggerSummary} loading={triggering}>
          Trigger Plan Summary Now
        </Button>
        <Text type="secondary" className="block mt-1" style={{ fontSize: 12 }}>
          Manually fires the plan summary for all eligible users right now, ignoring schedule.
        </Text>
      </Card>

      {/* Per-position preferences */}
      <Card
        title={
          <div className="flex items-center justify-between">
            <span>Position Notification Preferences</span>
            <Tag color="green">{positions.length} WhatsApp enabled</Tag>
          </div>
        }
      >
        {positions.length === 0 ? (
          <div className="text-center py-6">
            <Text type="secondary">No positions have WhatsApp enabled yet.</Text>
            <br />
            <Text type="secondary">Enable WhatsApp for positions in the Position Features page.</Text>
          </div>
        ) : (
          <Collapse accordion ghost>
            {positions.map(pos => {
              const formKey = pos.positionId;
              const prefs = pos.preferences;
              const initialVals = {
                frequencies: prefs?.frequencies || ['weekly'],
                performanceCycleIds: prefs?.performanceCycleIds || [],
                planSummaryEnabled: prefs?.planSummaryEnabled ?? true,
                approvalRemindersEnabled: prefs?.approvalRemindersEnabled ?? true,
                staleAlertEnabled: prefs?.staleAlertEnabled ?? true,
                completionAlertEnabled: prefs?.completionAlertEnabled ?? true,
              };

              return (
                <Panel
                  key={formKey}
                  header={
                    <div className="flex items-center gap-3">
                      <Avatar src={pos.imageUrl} icon={<UserOutlined />} size={32} />
                      <div>
                        <div className="font-medium">{pos.firstName} {pos.lastName}</div>
                        <div className="text-xs text-gray-500">{pos.designationName} · {pos.unitName}</div>
                      </div>
                      {pos.whatsappNumber
                        ? <Tag color="green" style={{ marginLeft: 'auto' }}>{pos.whatsappNumber}</Tag>
                        : <Tag color="red" style={{ marginLeft: 'auto' }}>No number set</Tag>
                      }
                    </div>
                  }
                >
                  <Form
                    layout="vertical"
                    initialValues={initialVals}
                    requiredMark={false}
                    onFinish={vals => onSavePositionPrefs(pos.positionId, vals)}
                    key={formKey}
                  >
                    <Form.Item name="frequencies" label="Notification Frequency">
                      <Checkbox.Group options={FREQUENCY_OPTIONS} />
                    </Form.Item>

                    <Form.Item
                      name="performanceCycleIds"
                      label="Performance Cycles"
                      extra="Leave empty to always use the currently active cycle"
                    >
                      <Select
                        mode="multiple"
                        placeholder="Select cycles (empty = active cycle)"
                        options={cycles}
                        allowClear
                      />
                    </Form.Item>

                    <Divider style={{ margin: '12px 0' }} />

                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item name="planSummaryEnabled" label="Plan Progress Summary" valuePropName="checked">
                          <Switch />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="approvalRemindersEnabled" label="Approval Reminders" valuePropName="checked">
                          <Switch />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="staleAlertEnabled" label="Stale Plan Alerts (14 days)" valuePropName="checked">
                          <Switch />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="completionAlertEnabled" label="Plan Completion Alerts" valuePropName="checked">
                          <Switch />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item className="mb-0">
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={savingPref[pos.positionId]}
                        icon={<SaveOutlined />}
                        disabled={!pos.whatsappNumber}
                      >
                        Save Preferences
                      </Button>
                      {!pos.whatsappNumber && (
                        <Text type="secondary" className="ml-3" style={{ fontSize: 12 }}>
                          Add a WhatsApp number to this user first
                        </Text>
                      )}
                    </Form.Item>
                  </Form>
                </Panel>
              );
            })}
          </Collapse>
        )}
      </Card>
    </div>
  );
};

export default WhatsAppConfig;
